/**
 * Resolve a bilaga reference to its displayed "Bilaga N" label.
 * Used by templates that have `attachment_ref` fields (per ADR-0028
 * addendum, Pattern B).
 *
 * Precedence:
 *   1. Slot-bound attachment → "Bilaga {fixedNumber}" (the template's
 *      `attachmentSlots[slotId].fixedNumber` baked in by the editor).
 *   2. Off-slot attachment → "Bilaga {ordinal + 1}" (the freeform
 *      number based on display order).
 *   3. Unknown id (user removed the attachment, or list not loaded
 *      yet) → the placeholder string.
 *
 * `attachments` may be undefined in dev gallery blank/preview modes
 * where there's no contract context — in that case every lookup
 * returns the placeholder, which renders the template's "Bilaga N"
 * blank as the expected mode='blank' style.
 */
export function attachmentLabel(attachments, id, placeholder = '____________') {
    if (!attachments || typeof id !== 'string' || id.length === 0) {
        return placeholder;
    }
    const a = attachments.find((x) => x.id === id);
    if (!a)
        return placeholder;
    return `Bilaga ${resolveNumber(a)}`;
}
/**
 * Normalise an `attachment_ref` field value to a clean id list. The value
 * is a single id string for single-reference fields and a `string[]` for
 * multi-reference fields (`attachmentMultiple`, per ADR-0028 addendum
 * 2026-05-27b); both shapes are tolerated everywhere so a field can be
 * widened to multi without a data migration. Empties are dropped.
 */
export function attachmentRefIds(value) {
    if (typeof value === 'string')
        return value.length > 0 ? [value] : [];
    if (Array.isArray(value)) {
        return value.filter((x) => typeof x === 'string' && x.length > 0);
    }
    return [];
}
/** True when an `attachment_ref` value points at at least one bilaga. */
export function hasAttachmentRef(value) {
    return attachmentRefIds(value).length > 0;
}
/**
 * Multi-reference label for `attachmentMultiple` fields: joins each
 * referenced bilaga's "Bilaga N" in number order with Swedish list
 * conjunction — "Bilaga 2", "Bilaga 2 och Bilaga 4", "Bilaga 2, Bilaga 4
 * och Bilaga 5". Accepts a single id or an array (back-compat). Returns
 * the placeholder when nothing resolves.
 */
export function attachmentLabels(attachments, ids, placeholder = '____________') {
    const resolved = attachmentRefIds(ids)
        .map((id) => attachments?.find((a) => a.id === id))
        .filter((a) => a != null)
        .sort((a, b) => resolveNumber(a) - resolveNumber(b))
        .map((a) => `Bilaga ${resolveNumber(a)}`);
    if (resolved.length === 0)
        return placeholder;
    if (resolved.length === 1)
        return resolved[0];
    return `${resolved.slice(0, -1).join(', ')} och ${resolved[resolved.length - 1]}`;
}
function resolveNumber(a) {
    // Prefer the server-resolved unified number (per ADR-0028 addendum
    // 2026-05-27); fall back to the legacy precedence for dev-gallery /
    // pre-migration callers that don't carry `number`.
    if (a.number != null)
        return a.number;
    if (a.slotFixedNumber != null)
        return a.slotFixedNumber;
    return a.ordinal + 1;
}
/** A bilaga's editable name (displayName), or the placeholder if unknown. */
export function attachmentName(attachments, id, placeholder = '____________') {
    if (!attachments || typeof id !== 'string' || id.length === 0) {
        return placeholder;
    }
    const a = attachments.find((x) => x.id === id);
    return a ? a.displayName : placeholder;
}
/**
 * Name-primary reference label (per the 2026-05-27 decision: references
 * lead with the bilaga's name; the number is a secondary locator).
 * Renders `{name} (Bilaga N)`, or just the placeholder when the
 * reference can't be resolved. Used for in-text clause references and
 * the editor's attachment picker — NOT the §Bilagor förteckning, which
 * stays a numbered list.
 */
export function attachmentRefLabel(attachments, id, placeholder = '____________') {
    if (!attachments || typeof id !== 'string' || id.length === 0) {
        return placeholder;
    }
    const a = attachments.find((x) => x.id === id);
    if (!a)
        return placeholder;
    return `${a.displayName} (Bilaga ${resolveNumber(a)})`;
}
//# sourceMappingURL=attachment-label.js.map